import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface ContactFormData {
  workEmail: string;
  fullName: string;
  companyWebsite: string;
  jobFunction: string;
  jobLevel: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    workEmail: '',
    fullName: '',
    companyWebsite: '',
    jobFunction: '',
    jobLevel: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const encode = (data: any) => {
        return Object.keys(data)
          .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
          .join("&");
      };

      console.log('Submitting form with data:', formData);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'contact-sales',
          'bot-field': '', // Honeypot field - must be empty
          ...formData
        })
      });

      console.log('Form response:', response.status, response.statusText);

      // Check if Netlify actually processed the form
      // A redirect or 200 response means success
      if (response.ok || response.status === 303) {
        console.log('Form submitted successfully to Netlify');
        setSubmitStatus('success');
        setFormData({
          workEmail: '',
          fullName: '',
          companyWebsite: '',
          jobFunction: '',
          jobLevel: '',
          message: ''
        });
      } else {
        console.error('Form submission failed:', response.status, response.statusText);
        const responseText = await response.text();
        console.error('Response body:', responseText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    'Schedule an AI architecture consultation',
    'Discuss multi-agent system design',
    'Explore custom LLM solutions'
  ];

  return (
    <section id="contact" style={{
      padding: '8rem 0',
      background: 'linear-gradient(180deg, #000000 0%, #0a0e27 100%)',
      position: 'relative'
    }}>
      <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 1.5rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '4rem'
        }}
        className="contact-grid">
          {/* Left Side - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <h2 style={{
              fontSize: 'clamp(2.25rem, 4vw, 3rem)',
              fontWeight: 'bold',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2'
            }}>
              Let's Build{' '}
              <span style={{
                background: 'linear-gradient(135deg, #00d4ff 0%, #b24bf3 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Intelligent Systems
              </span>
            </h2>
            <p style={{
              fontSize: '1.25rem',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '2rem',
              lineHeight: '1.75'
            }}>
              Ready to deploy AI agents at scale? Let's discuss your agent architecture,
              context management needs, and workflow automation goals.
            </p>
            
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {benefits.map((benefit, index) => (
                <motion.li
                  key={`benefit-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#ffffff'
                  }}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                >
                  <svg style={{ width: '1.25rem', height: '1.25rem', color: '#10b981', marginRight: '0.75rem' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {benefit}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form
              onSubmit={handleSubmit}
              className="cf-form"
              name="contact-sales"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value="contact-sales" />

              <p className="cf-honeypot">
                <label>
                  Don't fill this out if you're human: <input name="bot-field" />
                </label>
              </p>

              <div className="cf-field">
                <label htmlFor="workEmail" className="cf-label">
                  Work email
                </label>
                <input
                  type="email"
                  id="workEmail"
                  name="workEmail"
                  required
                  value={formData.workEmail}
                  onChange={handleInputChange}
                  className="cf-input"
                  placeholder="you@company.com"
                />
              </div>

              <div className="cf-row">
                <div className="cf-field">
                  <label htmlFor="fullName" className="cf-label">
                    Full name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="cf-input"
                    placeholder="Jane Doe"
                  />
                </div>

                <div className="cf-field">
                  <label htmlFor="companyWebsite" className="cf-label">
                    Company website <span className="cf-optional">Optional</span>
                  </label>
                  <input
                    type="url"
                    id="companyWebsite"
                    name="companyWebsite"
                    value={formData.companyWebsite}
                    onChange={handleInputChange}
                    className="cf-input"
                    placeholder="https://company.com"
                  />
                </div>
              </div>

              <div className="cf-row">
                <div className="cf-field">
                  <label htmlFor="jobFunction" className="cf-label">
                    Job function
                  </label>
                  <select
                    id="jobFunction"
                    name="jobFunction"
                    value={formData.jobFunction}
                    onChange={handleInputChange}
                    className="cf-select"
                  >
                    <option value="">Select a job function...</option>
                    <option value="ceo">CEO/Founder</option>
                    <option value="cto">CTO/Technical Lead</option>
                    <option value="engineering">Engineering</option>
                    <option value="product">Product Management</option>
                    <option value="marketing">Marketing</option>
                    <option value="sales">Sales</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="cf-field">
                  <label htmlFor="jobLevel" className="cf-label">
                    Job level
                  </label>
                  <select
                    id="jobLevel"
                    name="jobLevel"
                    value={formData.jobLevel}
                    onChange={handleInputChange}
                    className="cf-select"
                  >
                    <option value="">Select a job level...</option>
                    <option value="individual">Individual Contributor</option>
                    <option value="manager">Manager</option>
                    <option value="director">Director</option>
                    <option value="vp">VP</option>
                    <option value="c-level">C-Level</option>
                  </select>
                </div>
              </div>

              <div className="cf-field">
                <label htmlFor="message" className="cf-label">
                  Anything else? <span className="cf-optional">Optional</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="cf-textarea"
                  placeholder="Tell us more about your project, needs, timeline"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="cf-submit"
                whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.99 }}
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send message
                    <svg className="cf-submit-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </motion.button>

              {submitStatus === 'success' && (
                <motion.div
                  className="cf-alert cf-alert-success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p style={{ fontWeight: 600 }}>Thank you! Your message has been sent successfully.</p>
                  <p style={{ fontSize: '0.875rem', marginTop: '0.25rem', opacity: 0.85 }}>We'll get back to you soon.</p>
                  {process.env.NODE_ENV === 'development' && (
                    <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.7 }}>
                      Note: In development, form submissions are simulated. Deploy to Netlify to enable actual email notifications.
                    </p>
                  )}
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  className="cf-alert cf-alert-error"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  Sorry, there was an error sending your message. Please try again.
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;